import type { CSSObject } from '@mui/material/styles';

/**
 * The ticket silhouette: a concave bite out of each corner, and optionally a
 * pair of notches where the stub perforation meets the top and bottom edges.
 *
 * Taken from `_Dev process/Event card.svg`, which is 683.82 x 182.17 with a
 * notch radius of 12.6 and the perforation at x = 575.39.
 *
 * Implemented as a mask rather than background-colored circles so the notches
 * are genuinely transparent. That matters on My Schedule, where the cards sit
 * on a sky gradient and on user-chosen fill colors — a circle painted in the
 * page background would show as an opaque dot there.
 */

export interface NotchOptions {
  /** Notch radius: px, or any CSS length (the callout's are in container units). */
  radius?: number | string;
  /**
   * Which corners are bitten. All four by default; the callout's ticket runs
   * off the top of its column, and its two halves meet at the perforation.
   */
  corners?: ('tl' | 'tr' | 'bl' | 'br')[];
  /** Position of a vertical stub perforation, as a CSS length or percentage. */
  perforationAt?: string | null;
  /**
   * Position of a *horizontal* perforation, for the phone's card.
   *
   * The same ticket turned on its side: the stub is below the body rather than
   * beside it, because a phone has height to spare and no width at all.
   */
  perforationRow?: string | null;
  /**
   * A side torn along a perforation: half of each of its holes cut out of the
   * edge, as a stub leaves them when it is torn off. The desktop card's two
   * halves, once the event is on the schedule.
   */
  tornEdge?: 'left' | 'right' | null;
}

/** The torn edge's holes: their radius, and how far apart they fall, near the callout's perforation. */
const TORN_HOLE = 2.2;
const TORN_PITCH = 12;

const HOLE = (r: number | string, at: string) =>
  `radial-gradient(circle ${typeof r === 'number' ? `${r}px` : r} at ${at}, transparent 99%, #000 100%)`;

const CORNER_AT = { tl: '0 0', tr: '100% 0', bl: '0 100%', br: '100% 100%' } as const;

export function notchMask({
  radius = 11,
  corners = ['tl', 'tr', 'bl', 'br'],
  perforationAt = null,
  perforationRow = null,
  tornEdge = null,
}: NotchOptions = {}): CSSObject {
  const layers = corners.map((c) => HOLE(radius, CORNER_AT[c]));
  const sizes = layers.map(() => 'auto');
  const repeats = layers.map(() => 'no-repeat');

  const add = (layer: string, size = 'auto', repeat = 'no-repeat') => {
    layers.push(layer);
    sizes.push(size);
    repeats.push(repeat);
  };

  if (perforationAt) {
    add(HOLE(radius, `${perforationAt} 0`));
    add(HOLE(radius, `${perforationAt} 100%`));
  }

  if (perforationRow) {
    add(HOLE(radius, `0 ${perforationRow}`));
    add(HOLE(radius, `100% ${perforationRow}`));
  }

  // One hole per tile, centred on the edge, tiled down its length.
  if (tornEdge) {
    add(HOLE(TORN_HOLE, `${tornEdge === 'right' ? '100%' : '0'} 50%`), `100% ${TORN_PITCH}px`, 'repeat-y');
  }

  const image = layers.join(', ');
  // Every layer is opaque except its own hole, so intersecting them punches all
  // the holes at once. The prefixed property predates the standard one and uses
  // its own keyword set, hence the two different values.
  const composite = layers.map(() => 'intersect').join(', ');
  const webkitComposite = layers.map(() => 'source-in').join(', ');

  return {
    maskImage: image,
    WebkitMaskImage: image,
    maskComposite: composite,
    WebkitMaskComposite: webkitComposite,
    maskSize: sizes.join(', '),
    WebkitMaskSize: sizes.join(', '),
    maskRepeat: repeats.join(', '),
    WebkitMaskRepeat: repeats.join(', '),
  };
}
